import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/Dialog';
import { useOnboardingStore, OnboardingRequest, type InviteOutcome } from '../../../store/onboardingStore';
import { useEmployeeStore } from '../../../store/employeeStore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/Card';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Label } from '../../../components/Label';
import { Textarea } from '../../../components/Textarea';
import { CheckCircle2, Clock, AlertTriangle, Shield, UserCheck, XCircle, Plus, Check, Trash2, Mail, MailWarning, RotateCw } from 'lucide-react';
import toast from 'react-hot-toast';

export function OnboardingDashboard() {
  const { requests, fetchRequests, initiateOnboarding, resendInvite, approveRequest, rejectRequest, deleteRequest, isLoading } = useOnboardingStore();
  const { employees, fetchEmployees } = useEmployeeStore();

  const [deleteReqTarget, setDeleteReqTarget] = useState<{ id: string; label: string } | null>(null);
  const [deletingReq, setDeletingReq] = useState(false);

  const handleDeleteRequest = async () => {
    if (!deleteReqTarget) return;
    setDeletingReq(true);
    try {
      await deleteRequest(deleteReqTarget.id);
      toast.success('Onboarding request deleted');
      setDeleteReqTarget(null);
    } catch {
      toast.error('Failed to delete onboarding request');
    } finally {
      setDeletingReq(false);
    }
  };

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [newInvite, setNewInvite] = useState({ name: '', email: '', dept: '', desig: '', date: '' });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Approval Modal State
  const [selectedRequest, setSelectedRequest] = useState<OnboardingRequest | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Approval Form State
  const [approvalForm, setApprovalForm] = useState({
    department: 'Engineering',
    designation: 'Software Engineer',
    roleCode: 'employee',
    employmentType: 'regular',
    reportsTo: '',
    joiningDate: new Date().toISOString().split('T')[0],
    taskTitle: 'Complete Security & Tooling Setup',
    taskDescription: 'Set up MFA, review organization guidelines, and complete TeamOne workspace onboarding.',
    taskPriority: 'high',
    taskDueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchRequests();
    fetchEmployees({ limit: 100 });
  }, []);

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const activePipelines = requests.filter(r => r.status !== 'pending');

  const handleOpenApproveModal = (req: OnboardingRequest) => {
    setSelectedRequest(req);
    setApprovalForm({
      department: req.department || 'Engineering',
      designation: req.designation || 'Software Engineer',
      roleCode: 'employee',
      employmentType: 'regular',
      reportsTo: '',
      joiningDate: req.joiningDate ? new Date(req.joiningDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      taskTitle: 'Complete Security & Tooling Setup',
      taskDescription: 'Set up MFA, review organization guidelines, and complete TeamOne workspace onboarding.',
      taskPriority: 'high',
      taskDueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    });
    setShowApprovalModal(true);
  };

  const handleApproveSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await approveRequest(selectedRequest.id, approvalForm);
      toast.success(`Access approved for ${selectedRequest.candidateName || selectedRequest.email}. User account is now active.`);
      setShowApprovalModal(false);
      setSelectedRequest(null);
      await fetchRequests();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmit = async () => {
    if (!selectedRequest) return;
    setActionLoading(true);
    try {
      await rejectRequest(selectedRequest.id, rejectReason);
      toast.success(`Access request for ${selectedRequest.candidateName || selectedRequest.email} was rejected.`);
      setShowRejectModal(false);
      setShowApprovalModal(false);
      setSelectedRequest(null);
      setRejectReason('');
      await fetchRequests();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!newInvite.name || !newInvite.email) {
      toast.error('Name and Email are required.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newInvite.email.trim())) {
      toast.error('Please enter a valid email address.');
      return;
    }
    setSendingInvite(true);
    try {
      const email = newInvite.email.trim();
      const invite = await initiateOnboarding({
        candidateName: newInvite.name.trim(),
        email,
        department: newInvite.dept,
        designation: newInvite.desig,
        joiningDate: newInvite.date
      });
      reportInvite(email, invite, 'Onboarding started');
      setShowInviteModal(false);
      setNewInvite({ name: '', email: '', dept: '', desig: '', date: '' });
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSendingInvite(false);
    }
  };

  /** Tell HR what really happened — email and in-app delivery are reported separately. */
  const reportInvite = (email: string, invite: InviteOutcome | undefined, prefix: string) => {
    const inApp = invite?.notified ? ' and notified in TeamOne' : '';
    if (invite?.emailed) {
      toast.success(`${prefix}: invite emailed to ${email}${inApp}.`);
    } else if (invite?.emailStatus === 'not_configured') {
      toast.error(`${prefix}, but no email was sent: email is not configured on the server.${inApp ? ' They were notified in TeamOne.' : ''}`, { duration: 7000 });
    } else {
      toast.error(`${prefix}, but the email to ${email} failed: ${invite?.emailError || 'unknown error'}. Use Resend invite to try again.`, { duration: 8000 });
    }
  };

  const handleResend = async (r: OnboardingRequest) => {
    setResendingId(r.id);
    try {
      const invite = await resendInvite(r.id);
      reportInvite(r.email, invite, 'Invite resent');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setResendingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center bg-card p-6 rounded-lg border border-border">
        <div>
          <h1 className="tracking-tight text-foreground font-display text-page-title">
            Employee <span className="text-primary">Onboarding & Access Requests</span>
          </h1>
          <p className="text-muted-foreground text-xs mt-1">
            Review self-service access requests, assign roles & initial tasks, and manage onboarding pipelines.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => fetchRequests()}>Refresh</Button>
          <Button variant="primary" size="sm" onClick={() => setShowInviteModal(true)}>+ Initiate Onboarding</Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: 'Pending Access Requests', value: pendingRequests.length, color: pendingRequests.length > 0 ? 'warning' : 'secondary', delta: pendingRequests.length > 0 ? 'Requires Action' : 'All clear' },
          { label: 'Active Pipeline', value: activePipelines.length, color: 'primary', delta: 'In progress' },
          { label: 'Approved & Active', value: requests.filter(r => r.status === 'approved').length, color: 'success', delta: 'Activated' },
          { label: 'Completion Rate', value: '96%', color: 'info', delta: 'Target: 98%' },
        ].map((stat, i) => (
          <Card key={i} className="border-border bg-card">
            <CardContent className="p-5">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground">{stat.label}</p>
                  <p className="text-foreground font-display text-metric">{stat.value}</p>
                </div>
                <Badge variant={stat.color as any} className="text-xs">{stat.delta}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Access Requests Queue */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="size-8 rounded-md bg-warning/10 text-warning flex items-center justify-center border border-warning/20">
                <Clock className="size-4" />
              </div>
              <div>
                <CardTitle className="text-sm font-semibold text-foreground">
                  Pending Access Requests ({pendingRequests.length})
                </CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  New users requesting access. Review and assign their department, system role, and onboarding task to approve.
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {pendingRequests.length === 0 ? (
            <div className="py-10 text-center space-y-2">
              <div className="size-10 rounded-full bg-secondary/80 text-muted-foreground flex items-center justify-center mx-auto">
                <Check className="size-5" />
              </div>
              <p className="text-sm font-medium text-foreground">No Pending Access Requests</p>
              <p className="text-xs text-muted-foreground">When new organization members sign up or log in via SSO, their requests will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs">
                <thead className="bg-secondary/40 text-muted-foreground font-semibold border-b border-border">
                  <tr>
                    <th className="px-6 py-3">Applicant / Email</th>
                    <th className="px-6 py-3">Source</th>
                    <th className="px-6 py-3">Request Date</th>
                    <th className="px-6 py-3">Status</th>
                    <th className="px-6 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {pendingRequests.map((r) => (
                    <tr key={r.id} className="hover:bg-secondary/20 transition-colors">
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="size-8 rounded-md bg-warning/10 border border-warning/20 flex items-center justify-center font-semibold text-warning-strong">
                            {(r.candidateName || r.email)[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{r.candidateName || 'New Member'}</p>
                            <p className="text-xs text-muted-foreground font-mono">{r.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3.5">
                        <Badge variant="secondary" className="capitalize text-xs">
                          {r.source === 'hr_invite' ? 'HR invite' : r.source === 'register' ? 'Registered' : 'Google sign-in'}
                        </Badge>
                      </td>
                      <td className="px-6 py-3.5 text-muted-foreground">
                        {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : 'Today'}
                      </td>
                      <td className="px-6 py-3.5">
                        <Badge variant="warning" className="text-xs">
                          Awaiting Approval
                        </Badge>
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <Button 
                          variant="primary" 
                          size="sm"
                          onClick={() => handleOpenApproveModal(r)}
                          className="h-8 text-xs font-medium"
                        >
                          Review & Approve
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Main Content: Active Pipeline / All Onboarding Requests */}
      <Card className="border-border bg-card">
        <CardHeader className="pb-3 border-b border-border">
          <CardTitle className="text-sm font-semibold text-foreground">
            Onboarding Pipeline & Employees ({activePipelines.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead className="bg-secondary/40 text-muted-foreground font-semibold border-b border-border">
                <tr>
                  <th className="px-6 py-3">Employee / Candidate</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Dept / Designation</th>
                  <th className="px-6 py-3">Joining Date</th>
                  <th className="px-6 py-3">Progress</th>
                  <th className="px-6 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {activePipelines.map((r) => (
                  <tr key={r.id} className="hover:bg-secondary/20 transition-colors group">
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-3">
                        <div className="size-8 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center font-semibold text-primary">
                          {(r.candidateName || r.email)[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-foreground">{r.candidateName || 'Employee'}</p>
                          <p className="text-xs text-muted-foreground font-mono">{r.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3.5">
                      <Badge variant={
                        r.status === 'under-review' ? 'warning' :
                        r.status === 'initiated' ? 'primary' :
                        r.status === 'approved' ? 'success' :
                        r.status === 'rejected' ? 'destructive' : 'default'
                      } className="text-xs capitalize">
                        {r.status}
                      </Badge>
                      <InviteStatus r={r} />
                    </td>
                    <td className="px-6 py-3.5">
                      <p className="font-medium text-foreground">{r.department || '—'}</p>
                      <p className="text-muted-foreground truncate max-w-[150px]">{r.designation || '—'}</p>
                    </td>
                    <td className="px-6 py-3.5 text-muted-foreground">
                      {r.joiningDate ? new Date(r.joiningDate).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="w-24 flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-primary transition-all duration-700" 
                            style={{ width: `${Math.min(100, Math.round(((r.currentStep || 1) / 7) * 100))}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">
                          {Math.min(100, Math.round(((r.currentStep || 1) / 7) * 100))}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {!['approved', 'rejected', 'closed', 'withdrawn'].includes(r.status) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={() => handleResend(r)}
                            isLoading={resendingId === r.id}
                            title={r.inviteStatus ? 'Send the invite email again' : 'Email an invite'}
                          >
                            {resendingId !== r.id && <RotateCw className="size-3.5" />}
                            {r.inviteStatus ? 'Resend invite' : 'Send invite'}
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => window.location.href=`/people/onboarding/${r.id}`}
                        >
                          Details
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-muted-foreground hover:text-destructive"
                          aria-label="Delete onboarding request"
                          onClick={() => setDeleteReqTarget({ id: r.id, label: r.candidateName || r.email })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Review & Approve Modal */}
      <Dialog open={showApprovalModal} onOpenChange={setShowApprovalModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedRequest && (
            <form onSubmit={handleApproveSubmit} className="space-y-6">
              <DialogHeader>
                <div className="flex items-center gap-2.5">
                  <div className="size-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center border border-primary/20">
                    <UserCheck className="size-5" />
                  </div>
                  <div>
                    <DialogTitle className="text-base font-semibold">Review & Approve Access Request</DialogTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Configure organization role, placement, and initial task for {selectedRequest.candidateName || selectedRequest.email}.
                    </p>
                  </div>
                </div>
              </DialogHeader>

              {/* Applicant Info Box */}
              <div className="rounded-lg border border-border bg-secondary/30 p-3.5 text-xs grid grid-cols-2 gap-3">
                <div>
                  <span className="text-muted-foreground">Full Name:</span>
                  <p className="font-semibold text-foreground mt-0.5">{selectedRequest.candidateName || 'New Member'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Email Address:</span>
                  <p className="font-mono text-foreground mt-0.5">{selectedRequest.email}</p>
                </div>
              </div>

              {/* Assignment Fields */}
              <div className="space-y-4">
                <h3 className="text-foreground font-display text-card-title">
                  1. Organization Placement & Role
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="department" required>Department</Label>
                    <select
                      id="department"
                      value={approvalForm.department}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setApprovalForm({ ...approvalForm, department: e.target.value })}
                      className="flex h-10 w-full rounded-lg border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:border-primary"
                    >
                      <option value="Engineering">Engineering</option>
                      <option value="Product">Product</option>
                      <option value="Design">Design</option>
                      <option value="Marketing">Marketing</option>
                      <option value="Sales">Sales</option>
                      <option value="Operations">Operations</option>
                      <option value="Human Resources">Human Resources</option>
                      <option value="Finance">Finance</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="designation" required>Designation / Title</Label>
                    <Input
                      id="designation"
                      value={approvalForm.designation}
                      onChange={(e) => setApprovalForm({ ...approvalForm, designation: e.target.value })}
                      placeholder="e.g. Senior Frontend Developer"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="roleCode" required>System RBAC Role</Label>
                    <select
                      id="roleCode"
                      value={approvalForm.roleCode}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setApprovalForm({ ...approvalForm, roleCode: e.target.value })}
                      className="flex h-10 w-full rounded-lg border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:border-primary"
                    >
                      <option value="employee">Employee (Standard Access)</option>
                      <option value="reporting_manager">Reporting Manager</option>
                      <option value="hr_admin">HR Admin</option>
                      <option value="recruitment_admin">Recruitment Admin</option>
                      <option value="payroll_admin">Payroll Admin</option>
                      <option value="attendance_admin">Attendance Admin</option>
                      <option value="super_admin">Super Admin</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="employmentType" required>Employment Type</Label>
                    <select
                      id="employmentType"
                      value={approvalForm.employmentType}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setApprovalForm({ ...approvalForm, employmentType: e.target.value })}
                      className="flex h-10 w-full rounded-lg border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:border-primary"
                    >
                      <option value="regular">Regular (Full-time)</option>
                      <option value="contract">Contract</option>
                      <option value="freelancer">Freelancer</option>
                      <option value="part-time">Part-time</option>
                      <option value="intern">Intern</option>
                      <option value="consultant">Consultant</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="reportsTo">Reporting Manager</Label>
                    <select
                      id="reportsTo"
                      value={approvalForm.reportsTo}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setApprovalForm({ ...approvalForm, reportsTo: e.target.value })}
                      className="flex h-10 w-full rounded-lg border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:border-primary"
                    >
                      <option value="">None / Direct Report</option>
                      {employees.map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.displayName || `${emp.firstName} ${emp.lastName}`.trim()} ({emp.department || 'General'})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="joiningDate" required>Joining Date</Label>
                    <Input
                      id="joiningDate"
                      type="date"
                      value={approvalForm.joiningDate}
                      onChange={(e) => setApprovalForm({ ...approvalForm, joiningDate: e.target.value })}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Initial Task Assignment */}
              <div className="space-y-4 pt-2 border-t border-border">
                <h3 className="text-foreground font-display text-card-title">
                  2. Initial Onboarding Task
                </h3>

                <div className="space-y-1.5">
                  <Label htmlFor="taskTitle" required>Task Title</Label>
                  <Input
                    id="taskTitle"
                    value={approvalForm.taskTitle}
                    onChange={(e) => setApprovalForm({ ...approvalForm, taskTitle: e.target.value })}
                    placeholder="e.g. Complete Security Training & Workspace Setup"
                    required
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="taskDescription">Task Description</Label>
                  <Textarea
                    id="taskDescription"
                    value={approvalForm.taskDescription}
                    onChange={(e) => setApprovalForm({ ...approvalForm, taskDescription: e.target.value })}
                    placeholder="Detailed steps for the user..."
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="taskPriority">Priority</Label>
                    <select
                      id="taskPriority"
                      value={approvalForm.taskPriority}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setApprovalForm({ ...approvalForm, taskPriority: e.target.value })}
                      className="flex h-10 w-full rounded-lg border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:border-primary"
                    >
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="taskDueDate">Task Due Date</Label>
                    <Input
                      id="taskDueDate"
                      type="date"
                      value={approvalForm.taskDueDate}
                      onChange={(e) => setApprovalForm({ ...approvalForm, taskDueDate: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2 pt-4 border-t border-border">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setShowRejectModal(true)}
                  disabled={actionLoading}
                >
                  Reject Request
                </Button>
                <div className="flex-1" />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowApprovalModal(false)}
                  disabled={actionLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={actionLoading}
                >
                  Approve & Activate Account
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Rejection Confirmation Modal */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-2.5 text-destructive">
              <XCircle className="size-5" />
              <DialogTitle className="text-base font-semibold text-foreground">Reject Access Request</DialogTitle>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Are you sure you want to reject the access request for <span className="font-semibold text-foreground">{selectedRequest?.candidateName || selectedRequest?.email}</span>?
            </p>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <Label htmlFor="rejectReason">Rejection Reason (Optional)</Label>
            <Textarea
              id="rejectReason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Not an authorized organization contractor or incorrect domain."
              rows={3}
            />
          </div>

          <DialogFooter className="gap-2 pt-3 border-t border-border">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowRejectModal(false)}
              disabled={actionLoading}
            >
              Back
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleRejectSubmit}
              isLoading={actionLoading}
            >
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Invitation Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-lg">
          <div className="space-y-6">
            <DialogHeader>
              <DialogTitle>Initiate Onboarding</DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1 col-span-2">
                <Label>Full Name</Label>
                <Input 
                  placeholder="Full Name"
                  value={newInvite.name}
                  onChange={e => setNewInvite({...newInvite, name: e.target.value})}
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Email (the invite is sent here)</Label>
                <Input 
                  placeholder="candidate@email.com"
                  value={newInvite.email}
                  onChange={e => setNewInvite({...newInvite, email: e.target.value})}
                />
              </div>
              <div className="space-y-1 col-span-2">
                <Label>Designation</Label>
                <Input
                  placeholder="e.g. Software Engineer"
                  value={newInvite.desig}
                  onChange={e => setNewInvite({...newInvite, desig: e.target.value})}
                />
              </div>
              <div className="space-y-1">
                <Label>Department</Label>
                <Input 
                  placeholder="Marketing, etc."
                  value={newInvite.dept}
                  onChange={e => setNewInvite({...newInvite, dept: e.target.value})}
                />
              </div>
              <div className="space-y-1">
                <Label>Joining Date</Label>
                <Input 
                  type="date"
                  value={newInvite.date}
                  onChange={e => setNewInvite({...newInvite, date: e.target.value})}
                />
              </div>
            </div>

            <div className="pt-6 flex gap-3">
              <Button variant="secondary" className="flex-1" onClick={() => setShowInviteModal(false)}>Cancel</Button>
              <Button variant="primary" className="flex-1" onClick={handleInvite} isLoading={sendingInvite}>
                <Mail className="size-4" /> Send invite
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete onboarding request confirmation */}
      <Dialog open={!!deleteReqTarget} onOpenChange={(o) => !o && setDeleteReqTarget(null)}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete onboarding request</DialogTitle>
            <DialogDescription>
              Delete the onboarding request for <span className="font-medium text-foreground">{deleteReqTarget?.label}</span>? This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDeleteReqTarget(null)} disabled={deletingReq}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteRequest} isLoading={deletingReq}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Invite delivery line under the request status: sent / failed / not configured. */
function InviteStatus({ r }: { r: OnboardingRequest }) {
  if (!r.inviteStatus) return null;
  if (r.inviteStatus === 'sent') {
    return (
      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground" title={r.inviteSentAt ? new Date(r.inviteSentAt).toLocaleString() : undefined}>
        <Mail className="size-3" /> Invite emailed{r.inviteSentAt ? ` ${new Date(r.inviteSentAt).toLocaleDateString()}` : ''}
      </p>
    );
  }
  return (
    <p className="mt-1 flex items-center gap-1 text-xs text-destructive-strong" title={r.inviteError || undefined}>
      <MailWarning className="size-3" /> {r.inviteStatus === 'not_configured' ? 'Email not configured' : 'Invite email failed'}
    </p>
  );
}
